import { useState, useEffect, useCallback } from 'react';
import {
  subscribeSyncState,
  getSyncState,
  fullSync,
  updatePendingCount,
  SyncState,
  SyncStatus,
} from '@/lib/syncService';
import { addPendingAction, PendingAction } from '@/lib/offlineDB';

export type { SyncState, SyncStatus };

export function useSyncState() {
  const [state, setState] = useState<SyncState>(getSyncState);

  useEffect(() => {
    return subscribeSyncState(setState);
  }, []);

  const sync = useCallback(async () => {
    return fullSync();
  }, []);

  const refreshPendingCount = useCallback(async () => {
    return updatePendingCount();
  }, []);

  return {
    ...state,
    sync,
    refreshPendingCount,
  };
}

// Hook for offline-capable mutations
export function useOfflineMutation<T extends { id?: string }>(options: {
  entity: PendingAction['entity'];
  endpoint: string;
  onSuccess?: (data: T) => void;
  onError?: (error: Error) => void;
}) {
  const { entity, endpoint, onSuccess, onError } = options;
  const { isOnline } = useSyncState();

  const mutate = useCallback(async (
    data: T,
    action: 'create' | 'update' | 'delete' = 'create'
  ): Promise<{ success: boolean; offline: boolean }> => {
    // Generate ID if creating new item
    const itemData = action === 'create' && !data.id
      ? { ...data, id: crypto.randomUUID() }
      : data;

    if (isOnline) {
      try {
        const method = action === 'create' ? 'POST' :
                       action === 'update' ? 'PATCH' : 'DELETE';

        const response = await fetch(`/api${endpoint}`, {
          method,
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
          body: method !== 'DELETE' ? JSON.stringify(itemData) : undefined,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const result = await response.json();
        onSuccess?.(result);
        return { success: true, offline: false };
      } catch (error: any) {
        // Network error - save offline
        if (error.name === 'TypeError' || !navigator.onLine) {
          await addPendingAction({
            type: action,
            entity,
            endpoint,
            method: action === 'create' ? 'POST' :
                    action === 'update' ? 'PATCH' : 'DELETE',
            data: itemData,
          });
          onSuccess?.(itemData as T);
          return { success: true, offline: true };
        }
        onError?.(error);
        return { success: false, offline: false };
      }
    } else {
      // Offline - queue the action
      await addPendingAction({
        type: action,
        entity,
        endpoint,
        method: action === 'create' ? 'POST' :
                action === 'update' ? 'PATCH' : 'DELETE',
        data: itemData,
      });
      await updatePendingCount();
      onSuccess?.(itemData as T);
      return { success: true, offline: true };
    }
  }, [isOnline, entity, endpoint, onSuccess, onError]);

  return { mutate, isOnline };
}
